import React, { forwardRef, useState } from 'react';
import { Icon } from '../icon';
import tw from 'twin.macro';
import { CheckIcon, TimesIcon } from 'icons';
import { Small } from '../text';

const style = `text-input-front bg-input-back block text-sm placeholder-input-front placeholder-opacity-40 rounded-md py-1.5 px-2 mb-3 w-full 
border-icons border-opacity-30 border-[2px] ring-input-back focus:border-brand focus:ring-0`;

const styleSelector = `bg-input-back float-left mr-2 mt-1.5 pb-2 text-brand text-sm border-icons border-opacity-50 
focus:border-brand focus:ring-1 focus:ring-transparent focus:ring-offset-transparent`;

const forLabelId = (label) => label.toLowerCase().replaceAll(' ', '_');

const Label = ({ label, htmlFor, fieldLabel = false, hasInlineAsterisk }) => (
  <label
    htmlFor={htmlFor}
    css={[
      tw`block mb-0.5 cursor-pointer`,
      hasInlineAsterisk && tw`inline-block`,
      fieldLabel && tw`-mt-2`,
    ]}
  >
    <Small bold>{label}</Small>
  </label>
);

export const RequiredAstrix = ({ hasInlineAsterisk }) => {
  return hasInlineAsterisk ? (
    <div tw='inline-block ml-0.5 text-danger text-lg'>*</div>
  ) : (
    <div tw='absolute top-1 right-2 text-danger text-lg'>*</div>
  );
};

// eslint-disable-next-line react/display-name
const InputField = forwardRef(
  ({ type, id, placeholder, label, requiredField, name, ...restProps }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        placeholder={placeholder}
        id={id !== '' ? id : forLabelId(label)}
        required={requiredField}
        name={name}
        css={[
          type !== 'checkbox' && type !== 'radio' && tw`${style}`,
          (type === 'checkbox' || type === 'radio') && tw`${styleSelector}`,
          type === 'checkbox' && tw`rounded`,
          type === 'radio' && tw`rounded-full`,
          type === 'number' &&
            tw`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`,
        ]}
        {...restProps}
      />
    );
  },
);

// eslint-disable-next-line react/display-name
export const Input = forwardRef(
  (
    {
      type = 'text',
      id = '',
      placeholder = '',
      label = '',
      IconType = null,
      iconClick = null,
      requiredField,
      hasInlineAsterisk = false,
      ...restProps
    },
    ref,
  ) => {
    const inputField = (
      <InputField
        css={[!!IconType && tw`pr-8`]}
        ref={ref}
        type={type}
        id={id}
        placeholder={placeholder}
        label={label}
        requiredField={requiredField}
        name={forLabelId(id)}
        {...restProps}
      />
    );
    return (
      <div css={[tw`relative`, label && (type === 'checkbox' || type === 'radio') && tw`flex`]}>
        {label && type !== 'checkbox' && type !== 'radio' && (
          <Label
            label={label}
            htmlFor={forLabelId(id)}
            fieldLabel
            hasInlineAsterisk={hasInlineAsterisk}
          />
        )}
        {requiredField && <RequiredAstrix hasInlineAsterisk={hasInlineAsterisk} />}
        {!IconType && inputField}
        {IconType && (
          <div tw='relative flex items-center'>
            {inputField}
            <div
              css={[tw`absolute right-1 top-1`, iconClick !== null && tw`cursor-pointer`]}
              onClick={iconClick}
            >
              <Icon Type={IconType} />
            </div>
          </div>
        )}
        {label && (type === 'checkbox' || type === 'radio') && (
          <Label label={label} htmlFor={forLabelId(label)} />
        )}
      </div>
    );
  },
);

export const Select = ({ options, selected, requiredField, label = '', ...restProps }) => {
  return (
    <div tw='relative'>
      {requiredField && <RequiredAstrix />}
      {label && <Label label={label} htmlFor={forLabelId(label)} />}
      <select css={[tw`${style}`]} {...restProps} defaultValue={selected}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export const TextArea = ({
  children,
  id = '',
  label = '',
  placeholder = '',
  requiredField,
  rows = 5,
  ...restProps
}) => {
  return (
    <div tw='relative'>
      {requiredField && <RequiredAstrix />}
      {label && <Label label={label} htmlFor={forLabelId(label)} fieldLabel />}
      <textarea
        css={[tw`${style}`]}
        rows={rows}
        defaultValue={children}
        placeholder={placeholder}
        id={id !== '' ? id : forLabelId(label)}
        {...restProps}
      ></textarea>
    </div>
  );
};

export const Switch = ({
  id,
  isChecked = true,
  switchLeft = false,
  fullWidth = false,
  labels = { default: '', checked: '', info: '' },
  ...restProps
}) => {
  const [checked, setChecked] = useState(isChecked);
  const IconType = checked ? CheckIcon : TimesIcon;
  const switchSize = 5;

  return (
    <>
      <label
        htmlFor={id}
        css={[
          tw`flex items-center relative h-${switchSize} w-max my-0 transition-all duration-500`,
          fullWidth && tw`p-0 w-[95%] cursor-pointer`,
        ]}
      >
        <div
          css={[
            tw`flex flex-col`,
            switchLeft && tw`pl-${switchSize * 2 + 2}`,
            !switchLeft && tw`pr-${switchSize * 2 + 2}`,
          ]}
        >
          <span tw='font-bold'>{checked ? labels.checked : labels.default}</span>
          {labels.info !== '' ? (
            <small tw='font-sans line-clamp-1 -mt-0.5 opacity-75'>{labels.info}</small>
          ) : (
            ''
          )}
        </div>
        <input
          type='checkbox'
          id={id}
          className='sr-only'
          tw='opacity-0 w-full absolute cursor-pointer'
          defaultChecked={checked}
          onClick={() => {
            setChecked(!checked);
            if (restProps.onClick) restProps.onClick();
          }}
        />
        <div
          css={[
            tw`absolute top-0 bg-danger h-${switchSize} w-${switchSize * 2} cursor-pointer rounded-full transition-all duration-200`,
            checked && tw`bg-brand`,
            switchLeft && tw`left-0`,
            !switchLeft && tw`right-0`,
          ]}
        ></div>
        <span
          css={[
            tw`h-${switchSize} w-${switchSize} 
          bg-input-back text-danger absolute rounded-full top-0 flex border-2 border-danger justify-center items-center 
          transition-all transition-transform duration-200 -rotate-180 cursor-pointer`,
            switchLeft && !checked && tw`left-0`,
            switchLeft && checked && tw`text-brand border-brand rotate-0 left-${switchSize}`,
            !switchLeft && !checked && tw`right-${switchSize}`,
            !switchLeft && checked && tw`text-brand border-brand rotate-0 right-0`,
          ]}
        >
          <IconType css={[tw`w-${switchSize - 2} h-${switchSize - 2}`]} />
        </span>
      </label>
    </>
  );
};
